import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, endpoints } from "../lib/api";
import Topbar, { PrimaryAction } from "../components/Topbar";
import { AppointmentStatusBadge } from "../components/StatusBadge";
import { timeOnly, euro } from "../lib/format";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
function fmtDayLabel(d) {
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit" });
}
function fmtRange(a, b) {
  return `${a.toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} — ${b.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}`;
}

export default function Agenda() {
  const qc = useQueryClient();
  const [view, setView] = useState("semana"); // semana | mes
  const [cursor, setCursor] = useState(() => startOfWeek(new Date()));
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const rangeStart = view === "semana" ? startOfWeek(cursor) : new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const rangeEnd = view === "semana" ? addDays(rangeStart, 7) : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59);

  const { data: appts = [] } = useQuery({
    queryKey: ["appointments", rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: () =>
      api.get(endpoints.appointments, {
        params: { from_iso: rangeStart.toISOString(), to_iso: rangeEnd.toISOString() },
      }).then((r) => r.data),
  });

  const goPrev = () => setCursor(view === "semana" ? addDays(cursor, -7) : new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(view === "semana" ? addDays(cursor, 7) : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(view === "semana" ? startOfWeek(new Date()) : new Date());

  return (
    <div>
      <Topbar
        title="Agenda"
        subtitle="Vista semanal y mensual de las reservas. Gestiona citas, profesionales y salas."
        actions={<PrimaryAction label="Nueva cita" onClick={() => setCreateOpen(true)} testId="btn-new-appointment" />}
      />

      {/* Controls */}
      <div className="laka-card p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6" data-testid="agenda-controls">
        <div className="flex items-center gap-2">
          <Button data-testid="agenda-prev" variant="ghost" size="icon" onClick={goPrev} className="rounded-full border laka-hairline">
            <ChevronLeft size={16} strokeWidth={1.5} />
          </Button>
          <Button data-testid="agenda-today" variant="ghost" onClick={goToday} className="text-xs uppercase tracking-wider">Hoy</Button>
          <Button data-testid="agenda-next" variant="ghost" size="icon" onClick={goNext} className="rounded-full border laka-hairline">
            <ChevronRight size={16} strokeWidth={1.5} />
          </Button>
          <div className="ml-3 font-display text-xl">
            {view === "semana"
              ? fmtRange(rangeStart, addDays(rangeStart, 6))
              : cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
          </div>
        </div>

        <div className="flex items-center gap-1 border rounded-full p-1 laka-hairline" data-testid="agenda-view-switch">
          {["semana", "mes"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`agenda-view-${v}`}
              className={`text-xs uppercase tracking-wider px-4 py-1.5 rounded-full transition-all ${
                view === v ? "bg-[#1A1A1A] text-[#FBF6F2]" : "text-[#4A4A4A] hover:text-[#1A1A1A]"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === "semana" ? (
        <WeekView start={rangeStart} appts={appts} onSelect={setEditing} />
      ) : (
        <MonthView monthStart={rangeStart} appts={appts} onSelect={setEditing} />
      )}

      <AppointmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => qc.invalidateQueries({ queryKey: ["appointments"] })}
      />

      <AppointmentDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        appointment={editing}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["appointments"] });
          setEditing(null);
        }}
      />
    </div>
  );
}

function WeekView({ start, appts, onSelect }) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const today = new Date();
  const byDay = useMemo(() => {
    const map = new Map(days.map((d) => [d.toDateString(), []]));
    appts.forEach((a) => {
      const d = new Date(a.starts_at);
      const key = d.toDateString();
      if (map.has(key)) map.get(key).push(a);
    });
    map.forEach((list) => list.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
    return map;
  }, [days, appts]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="agenda-week-view">
      {days.map((d) => {
        const list = byDay.get(d.toDateString()) || [];
        const isToday = sameDay(d, today);
        return (
          <div key={d.toISOString()} className={`laka-card p-4 min-h-[220px] ${isToday ? "ring-1 ring-[#D6B36A]" : ""}`}>
            <div className="flex items-baseline justify-between mb-3">
              <span className="laka-eyebrow" style={{ color: isToday ? "#D6B36A" : undefined }}>{fmtDayLabel(d)}</span>
              <span className="text-[10px]" style={{ color: "var(--laka-text)" }}>{list.length}</span>
            </div>
            <div className="space-y-2">
              {list.length === 0 && (
                <div className="text-xs py-6 text-center" style={{ color: "var(--laka-text)" }}>Sin citas</div>
              )}
              {list.map((a) => (
                <button
                  key={a.id}
                  onClick={() => onSelect(a)}
                  data-testid={`appt-${a.id}`}
                  className="w-full text-left border laka-hairline rounded-md p-2.5 hover:border-[#D6B36A] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{timeOnly(a.starts_at)}</div>
                    <AppointmentStatusBadge status={a.status} />
                  </div>
                  <div className="text-xs mt-1 truncate">{a.client_name}</div>
                  <div className="text-[11px] truncate" style={{ color: "var(--laka-text)" }}>{a.service_name}</div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ monthStart, appts, onSelect }) {
  const firstOffset = (monthStart.getDay() + 6) % 7;
  const totalDays = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstOffset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      const d = new Date(a.starts_at);
      const key = d.toDateString();
      (map[key] ||= []).push(a);
    });
    return map;
  }, [appts]);

  const today = new Date();

  return (
    <div className="laka-card p-4" data-testid="agenda-month-view">
      <div className="grid grid-cols-7 gap-px" style={{ backgroundColor: "#E8DFD1" }}>
        {["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"].map((h) => (
          <div key={h} className="bg-white p-2 text-[10px] tracking-[0.18em] uppercase text-center" style={{ color: "var(--laka-text)" }}>{h}</div>
        ))}
        {cells.map((c, idx) => {
          if (!c) return <div key={`empty-${idx}`} className="bg-white min-h-[100px]" />;
          const list = byDay[c.toDateString()] || [];
          const isToday = sameDay(c, today);
          return (
            <div key={c.toISOString()} className="bg-white min-h-[100px] p-2 hover:bg-[#FBF6F2] transition-colors">
              <div className={`text-xs font-medium ${isToday ? "text-[#D6B36A]" : ""}`}>{c.getDate()}</div>
              <div className="mt-1 space-y-1">
                {list.slice(0, 3).map((a) => (
                  <button
                    key={a.id}
                    onClick={() => onSelect(a)}
                    className="block w-full text-left text-[10px] px-1.5 py-1 rounded truncate"
                    style={{ backgroundColor: "#F5EFE7", color: "#1A1A1A" }}
                  >
                    {timeOnly(a.starts_at)} · {a.client_name.split(" ")[0]}
                  </button>
                ))}
                {list.length > 3 && (
                  <div className="text-[10px]" style={{ color: "var(--laka-text)" }}>+{list.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppointmentDialog({ open, onOpenChange, appointment, onSaved }) {
  const qc = useQueryClient();
  const isEdit = !!appointment;

  const { data: clients = [] } = useQuery({ queryKey: ["clients-min"], queryFn: () => api.get(endpoints.clients).then((r) => r.data) });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get(endpoints.services).then((r) => r.data) });
  const { data: pros = [] } = useQuery({ queryKey: ["professionals"], queryFn: () => api.get(endpoints.professionals).then((r) => r.data) });
  const { data: rooms = [] } = useQuery({ queryKey: ["rooms"], queryFn: () => api.get(endpoints.rooms).then((r) => r.data) });

  const [form, setForm] = useState(() => defaultForm(appointment));
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => { setForm(defaultForm(appointment)); }, [appointment, defaultForm]);

  const saveMut = useMutation({
    mutationFn: (payload) =>
      isEdit
        ? api.put(endpoints.appointment(appointment.id), payload).then((r) => r.data)
        : api.post(endpoints.appointments, payload).then((r) => r.data),
    onSuccess: () => {
      toast.success(isEdit ? "Cita actualizada" : "Cita creada");
      onSaved?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar la cita"),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(endpoints.appointment(appointment.id)),
    onSuccess: () => {
      toast.success("Cita cancelada");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      onOpenChange(false);
    },
  });

  const submit = () => {
    if (!form.client_id) return toast.error("Selecciona una clienta");
    if (!form.service_id) return toast.error("Selecciona un servicio");
    if (!form.starts_at) return toast.error("Selecciona fecha y hora");
    const iso = new Date(form.starts_at).toISOString();
    saveMut.mutate({
      client_id: form.client_id, service_id: form.service_id,
      professional: form.professional, room: form.room,
      starts_at: iso, status: form.status, notes: form.notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{isEdit ? "Editar cita" : "Nueva cita"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Clienta</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
              <SelectTrigger data-testid="appt-client-select"><SelectValue placeholder="Seleccionar clienta" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Servicio</Label>
            <Select value={form.service_id} onValueChange={(v) => set("service_id", v)}>
              <SelectTrigger data-testid="appt-service-select"><SelectValue placeholder="Seleccionar servicio" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {euro(s.price)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Profesional</Label>
              <Select value={form.professional} onValueChange={(v) => set("professional", v)}>
                <SelectTrigger data-testid="appt-professional-select"><SelectValue /></SelectTrigger>
                <SelectContent>{pros.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sala</Label>
              <Select value={form.room} onValueChange={(v) => set("room", v)}>
                <SelectTrigger data-testid="appt-room-select"><SelectValue /></SelectTrigger>
                <SelectContent>{rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha y hora</Label>
              <Input
                data-testid="appt-datetime"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => set("starts_at", e.target.value)}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmada">Confirmada</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="completada">Completada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea data-testid="appt-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Observaciones sobre la cita…" />
          </div>
        </div>
        <DialogFooter className="mt-4 flex justify-between sm:justify-between">
          {isEdit ? (
            <Button variant="ghost" onClick={() => deleteMut.mutate()} data-testid="appt-delete" className="text-red-600">
              Cancelar cita
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="appt-close">Cerrar</Button>
            <Button onClick={submit} disabled={saveMut.isPending} className="laka-btn-primary" data-testid="appt-submit">
              {isEdit ? "Guardar cambios" : "Crear cita"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultForm(a) {
  if (!a) {
    const d = new Date();
    d.setMinutes(0); d.setSeconds(0); d.setMilliseconds(0);
    d.setHours(d.getHours() + 1);
    return {
      client_id: "", service_id: "", professional: "Ana Ríos", room: "Sala Ivory",
      starts_at: d.toISOString().slice(0, 16), status: "confirmada", notes: "",
    };
  }
  return {
    client_id: a.client_id, service_id: a.service_id || "",
    professional: a.professional, room: a.room,
    starts_at: new Date(a.starts_at).toISOString().slice(0, 16),
    status: a.status, notes: a.notes || "",
  };
}
